/**
 * Auto Bonus Product Manager
 * Automatically adds/removes a bonus product (variant ID: 43496797438087) 
 * when cart has items or becomes empty
 */

(function() {
  const BONUS_VARIANT_ID = 43496797438087;
  let isProcessing = false;
  let lastCartState = null;
  let lastError = null;
  let errorCount = 0;
  const MAX_ERRORS = 3; // Stop trying after 3 errors

  /**
   * Check if bonus product is in cart
   */
  function isBonusProductInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(item => item.id === BONUS_VARIANT_ID);
  }

  /**
   * Count regular items (excluding bonus product)
   */
  function getRegularItemCount(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.filter(item => item.id !== BONUS_VARIANT_ID).length;
  }

  /**
   * Add bonus product to cart
   */
  function addBonusProduct() {
    if (isProcessing) {
      console.log('[Auto Bonus] Already processing, skipping add');
      return Promise.resolve();
    }

    // Check if we've had too many errors
    if (errorCount >= MAX_ERRORS) {
      console.error('[Auto Bonus] Too many errors (' + errorCount + '), stopping attempts. Last error:', lastError);
      console.error('[Auto Bonus] Please check if variant ID ' + BONUS_VARIANT_ID + ' exists and is available.');
      return Promise.resolve();
    }

    isProcessing = true;
    console.log('[Auto Bonus] Adding bonus product:', BONUS_VARIANT_ID);

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: BONUS_VARIANT_ID,
        quantity: 1
      })
    })
    .then(response => {
      console.log('[Auto Bonus] Response status:', response.status);
      return response.json().then(data => ({ status: response.status, data: data }));
    })
    .then(({ status, data }) => {
      console.log('[Auto Bonus] Response data:', data);
      
      // Check for errors
      if (status === 422 || data.status === 422 || data.errors || data.message) {
        errorCount++;
        lastError = data.errors || data.message || data.description || 'Unknown error';
        console.error('[Auto Bonus] ❌ ERROR #' + errorCount + ':', lastError);
        console.error('[Auto Bonus] This usually means:');
        console.error('  - Variant ID ' + BONUS_VARIANT_ID + ' does not exist');
        console.error('  - Product is out of stock');
        console.error('  - Product has purchase restrictions');
        console.error('[Auto Bonus] Please verify the variant ID in Shopify admin');
        
        if (errorCount >= MAX_ERRORS) {
          console.error('[Auto Bonus] ⚠️ Stopping after ' + MAX_ERRORS + ' failed attempts');
        }
        return null;
      } else {
        // Success! Reset error count
        errorCount = 0;
        lastError = null;
        console.log('[Auto Bonus] ✅ Bonus product added successfully');
        
        // Refresh cart drawer to show new item
        if (typeof CartDrawerInit === 'function') {
          setTimeout(() => CartDrawerInit(), 100);
        }
        
        return data;
      }
    })
    .catch(error => {
      errorCount++;
      lastError = error.message;
      console.error('[Auto Bonus] ❌ Network error #' + errorCount + ':', error);
    })
    .finally(() => {
      setTimeout(() => {
        isProcessing = false;
      }, 500);
    });
  }

  /**
   * Remove bonus product from cart
   */
  function removeBonusProduct(cart) {
    if (isProcessing) {
      console.log('[Auto Bonus] Already processing, skipping remove');
      return Promise.resolve();
    }

    // Find the bonus product in cart
    const bonusItem = cart.items.find(item => item.id === BONUS_VARIANT_ID);
    if (!bonusItem) {
      console.log('[Auto Bonus] Bonus product not found in cart');
      return Promise.resolve();
    }

    isProcessing = true;
    console.log('[Auto Bonus] Removing bonus product:', BONUS_VARIANT_ID);

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: bonusItem.key,
        quantity: 0
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('[Auto Bonus] Bonus product removed successfully');
      // Refresh cart drawer to reflect removal
      if (typeof CartDrawerInit === 'function') {
        setTimeout(() => CartDrawerInit(), 100);
      }
      return data;
    })
    .catch(error => {
      console.error('[Auto Bonus] Error removing bonus product:', error);
    })
    .finally(() => {
      isProcessing = false;
    });
  }

  /**
   * Process cart and manage bonus product
   */
  function processCart(cart) {
    if (!cart) {
      console.log('[Auto Bonus] No cart data provided');
      return;
    }

    const regularItemCount = getRegularItemCount(cart);
    const hasBonusProduct = isBonusProductInCart(cart);

    console.log('[Auto Bonus] Cart state - Regular items:', regularItemCount, 'Has bonus:', hasBonusProduct);

    // Check if state actually changed to prevent unnecessary attempts
    if (lastCartState && 
        lastCartState.regularItemCount === regularItemCount && 
        lastCartState.hasBonusProduct === hasBonusProduct) {
      console.log('[Auto Bonus] No state change, skipping');
      return;
    }

    // Add bonus product if cart has items but bonus is missing
    if (regularItemCount > 0 && !hasBonusProduct) {
      // Only try if we haven't exceeded error limit
      if (errorCount < MAX_ERRORS) {
        console.log('[Auto Bonus] Cart has items, adding bonus product');
        addBonusProduct();
      } else {
        console.log('[Auto Bonus] Error limit reached, not attempting to add');
      }
    }
    // Remove bonus product if cart is empty (only bonus product remains)
    else if (regularItemCount === 0 && hasBonusProduct) {
      console.log('[Auto Bonus] Cart is empty, removing bonus product');
      removeBonusProduct(cart);
    }
    else {
      console.log('[Auto Bonus] No action needed');
    }

    // Store last state
    lastCartState = {
      regularItemCount: regularItemCount,
      hasBonusProduct: hasBonusProduct
    };
  }

  /**
   * Fetch current cart and process
   */
  function checkCart() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        processCart(cart);
      })
      .catch(error => {
        console.error('[Auto Bonus] Error fetching cart:', error);
      });
  }

  /**
   * Initialize event listeners
   */
  function init() {
    console.log('[Auto Bonus] Initializing auto bonus product manager');

    // Listen for cart updates (most reliable event)
    document.addEventListener('cart:updated', function(event) {
      console.log('[Auto Bonus] Cart updated event received');
      if (event.detail && event.detail.cart) {
        setTimeout(() => processCart(event.detail.cart), 200);
      } else {
        setTimeout(checkCart, 200);
      }
    });

    // Listen for product additions
    document.addEventListener('ajaxProduct:added', function(event) {
      console.log('[Auto Bonus] Product added event received');
      setTimeout(checkCart, 300);
    });

    // Listen for cart builds (drawer opening)
    document.addEventListener('cart:build', function() {
      console.log('[Auto Bonus] Cart build event received');
      setTimeout(checkCart, 200);
    });

    // Listen for cart changes
    document.addEventListener('cart:change', function(event) {
      console.log('[Auto Bonus] Cart change event received');
      setTimeout(checkCart, 200);
    });

    // Check cart on page load - IMMEDIATE
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        console.log('[Auto Bonus] DOM loaded, checking cart');
        setTimeout(checkCart, 100);
        setTimeout(checkCart, 500);
        setTimeout(checkCart, 1000);
      });
    } else {
      console.log('[Auto Bonus] DOM already loaded, checking cart immediately');
      setTimeout(checkCart, 100);
      setTimeout(checkCart, 500);
      setTimeout(checkCart, 1000);
    }

    // Observe cart drawer for manual changes (quantity updates, removals)
    function observeCartDrawer() {
      const cartDrawer = document.querySelector('#CartDrawer');
      if (cartDrawer) {
        const observer = new MutationObserver(function(mutations) {
          // Check if there were significant changes to cart items
          const hasCartChanges = mutations.some(mutation => {
            return mutation.type === 'childList' && 
                   (mutation.target.matches('[data-products]') || 
                    mutation.target.closest('[data-products]'));
          });

          if (hasCartChanges) {
            console.log('[Auto Bonus] Cart drawer mutation detected');
            setTimeout(checkCart, 300);
          }

          // Also check when drawer is opened
          const drawerOpened = mutations.some(mutation => {
            return mutation.type === 'attributes' && 
                   mutation.attributeName === 'class' &&
                   mutation.target.classList.contains('drawer--is-open');
          });

          if (drawerOpened) {
            console.log('[Auto Bonus] Cart drawer opened');
            setTimeout(checkCart, 100);
          }
        });

        observer.observe(cartDrawer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      }
    }

    // Try to observe now and retry if element not found
    observeCartDrawer();
    setTimeout(observeCartDrawer, 1000);
    setTimeout(observeCartDrawer, 2000);

    // Also watch for cart page changes (if on cart page)
    const cartPage = document.querySelector('#CartPageForm');
    if (cartPage) {
      const observer = new MutationObserver(function(mutations) {
        const hasCartChanges = mutations.some(mutation => {
          return mutation.type === 'childList';
        });

        if (hasCartChanges) {
          console.log('[Auto Bonus] Cart page mutation detected');
          setTimeout(checkCart, 300);
        }
      });

      observer.observe(cartPage, {
        childList: true,
        subtree: true
      });
    }

    // Listen for ANY add to cart button clicks
    document.addEventListener('click', function(event) {
      const target = event.target.closest('button, a, [role="button"]');
      if (target) {
        const isAddToCart = 
          target.classList.contains('add-to-cart') ||
          target.classList.contains('add-to-cart_bundle') ||
          target.id === 'lux-add-to-cart' ||
          target.id === 'syRollOnsAddToCartButton' ||
          target.hasAttribute('add-to-cart-bundle') ||
          target.textContent.toLowerCase().includes('add to cart');
        
        if (isAddToCart) {
          console.log('[Auto Bonus] Add to cart button clicked, checking cart');
          setTimeout(checkCart, 500);
          setTimeout(checkCart, 1000);
          setTimeout(checkCart, 1500);
        }
      }
    }, true);

    // Periodic check every 5 seconds as fallback (only if cart exists)
    setInterval(function() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (cart.item_count > 0) {
            const regularItemCount = getRegularItemCount(cart);
            const hasBonusProduct = isBonusProductInCart(cart);
            
            // Only process if state changed or bonus is missing
            if (regularItemCount > 0 && !hasBonusProduct) {
              console.log('[Auto Bonus] Periodic check found missing bonus product');
              processCart(cart);
            }
          }
        })
        .catch(error => {
          // Silent fail
        });
    }, 5000);
  }

  // Expose global test function for debugging
  window.testBonusProduct = function() {
    console.log('[Auto Bonus] Manual test triggered');
    checkCart();
  };

  window.forceAddBonusProduct = function() {
    console.log('[Auto Bonus] Force add triggered (ignoring error count)');
    errorCount = 0; // Reset errors for manual attempt
    addBonusProduct();
  };

  window.resetBonusProductErrors = function() {
    console.log('[Auto Bonus] Resetting error count');
    errorCount = 0;
    lastError = null;
    console.log('[Auto Bonus] ✅ Error count reset. You can try again now.');
  };

  window.getBonusProductStatus = function() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('[Auto Bonus] ═══════════════════════════════════');
        console.log('[Auto Bonus] 🛒 CART STATUS:');
        console.log('[Auto Bonus] ───────────────────────────────────');
        console.log('[Auto Bonus] Total items:', cart.item_count);
        console.log('[Auto Bonus] Regular items (excluding bonus):', getRegularItemCount(cart));
        console.log('[Auto Bonus] Has bonus product:', isBonusProductInCart(cart));
        console.log('[Auto Bonus] ───────────────────────────────────');
        console.log('[Auto Bonus] 🔧 SYSTEM STATUS:');
        console.log('[Auto Bonus] Is processing:', isProcessing);
        console.log('[Auto Bonus] Error count:', errorCount + '/' + MAX_ERRORS);
        if (lastError) {
          console.log('[Auto Bonus] Last error:', lastError);
        }
        console.log('[Auto Bonus] ───────────────────────────────────');
        console.log('[Auto Bonus] 📦 ALL CART ITEMS:');
        cart.items.forEach((item, index) => {
          console.log('[Auto Bonus] ' + (index + 1) + '. ' + item.title + ' (ID: ' + item.id + ')');
        });
        console.log('[Auto Bonus] ═══════════════════════════════════');
        
        if (errorCount >= MAX_ERRORS) {
          console.log('[Auto Bonus] ⚠️ ERROR LIMIT REACHED');
          console.log('[Auto Bonus] To try again: resetBonusProductErrors()');
        }
      });
  };

  window.checkBonusVariant = function(variantId) {
    const id = variantId || BONUS_VARIANT_ID;
    console.log('[Auto Bonus] Checking if variant ' + id + ' exists and is available...');
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: id,
        quantity: 0 // Try adding 0 to just check if it exists
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 422 || data.errors) {
        console.error('[Auto Bonus] ❌ Variant check failed:', data.errors || data.message);
        console.error('[Auto Bonus] This variant is NOT available');
      } else {
        console.log('[Auto Bonus] ✅ Variant appears to be valid');
      }
    })
    .catch(error => {
      console.error('[Auto Bonus] Network error:', error);
    });
  };

  // Start the manager
  console.log('[Auto Bonus] ═══════════════════════════════════════════════');
  console.log('[Auto Bonus] 🎉 Script loaded and ready!');
  console.log('[Auto Bonus] 🎯 Target Variant ID:', BONUS_VARIANT_ID);
  console.log('[Auto Bonus] ═══════════════════════════════════════════════');
  console.log('[Auto Bonus] 📋 Available Commands:');
  console.log('[Auto Bonus]   • getBonusProductStatus()    - Check current status');
  console.log('[Auto Bonus]   • testBonusProduct()         - Trigger manual check');
  console.log('[Auto Bonus]   • forceAddBonusProduct()     - Force add the product');
  console.log('[Auto Bonus]   • resetBonusProductErrors()  - Reset error counter');
  console.log('[Auto Bonus]   • checkBonusVariant()        - Verify variant exists');
  console.log('[Auto Bonus] ═══════════════════════════════════════════════');
  
  init();
})();

